import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../../services/apiClient';
import { consultationService } from '../../../../services/consultationService';
import { consultationLabOrderService } from '../../../../services/consultationLabOrderService';
import { consultationImagingOrderService } from '../../../../services/consultationImagingOrderService';
import { preAuthorizationService, type PreAuthorization } from '../../../../services/preAuthorizationService';
import { patientService, Patient as ServicePatient } from '../../../../services/patientService';
import { doctorService } from '../../../../services/doctorService';
import { diagnosisService } from '../../../../services/diagnosisService';
import { clinicalDelegationService, type AssignedDoctor } from '../../../../services/clinicalDelegationService';
import { useAuth } from '../../../../contexts/AuthContext';
import type { Doctor, Diagnosis } from '../../../../types/api';
import type { CreateConsultationRequest } from '../../../../types/api';
import type { ReviewOfSystem } from '../../types/consultation.types';
import {
  type DiagnosisEntry,
  type PrescriptionEntry,
  type LabOrderEntry,
  type ImagingOrderEntry,
  type VitalSignsEntry,
  emptyVitalSigns,
} from './types';

const REVIEW_OF_SYSTEMS_CODES = [
  'CONSTITUTIONAL', 'EYES', 'ORL', 'CARDIOVASCULAR', 'RESPIRATORY',
  'GASTROINTESTINAL', 'GENITOURINARY', 'MUSCULOSKELETAL', 'NEUROLOGICAL',
  'PSYCHIATRIC', 'SKIN', 'ENDOCRINE', 'HEMATOLOGIC', 'ALLERGIC_IMMUNOLOGIC',
] as const;

const getDefaultReviewOfSystems = (): ReviewOfSystem[] =>
  REVIEW_OF_SYSTEMS_CODES.map(code => ({
    system_code: code,
    has_findings: false,
    findings_description: '',
  }));

export const useConsultationFormBase = (editId?: number) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [rosSaveWarning, setRosSaveWarning] = useState(false);
  const isEditMode = !!editId;
  const DRAFT_KEY = `consultation_draft_${isEditMode ? 'edit_' + editId : 'new'}`;

  // clinical-delegation R3: only an actual "Asistente Médico" gets the
  // restricted/inert selector; every other profile (doctor, admin,
  // reception) keeps today's full-list behavior even though their own
  // my-doctors call also comes back empty (they're simply not an assistant).
  const isAssistant = user?.profile?.name === 'Asistente Médico';

  // The consultation being edited may belong to a doctor who is no longer
  // in `myDoctors` (assignment revoked) or isn't the current user's own
  // doctor — captured from the loaded record itself, not from `user.doctor`,
  // so the selector always has an option for it and never renders blank.
  const [loadedDoctor, setLoadedDoctor] = useState<Doctor | null>(null);

  // Form fields
  const [patientId, setPatientId] = useState<number | null>(null);
  // Display text of the patient of the consultation being edited, set as soon
  // as it is loaded so the selected-patient line never shows without a name.
  const [loadedPatientLabel, setLoadedPatientLabel] = useState('');
  // Bumped whenever the label is invalidated (new load, "Cambiar", other
  // consultation, unmount) so a late patient fetch cannot apply a stale label.
  const patientLabelSeq = useRef(0);
  const clearLoadedPatientLabel = () => {
    patientLabelSeq.current++;
    setLoadedPatientLabel('');
  };
  useEffect(() => {
    clearLoadedPatientLabel();
    return () => { patientLabelSeq.current++; };
  }, [editId]);
  const [doctorId, setDoctorId] = useState<number | null>(user?.doctor?.id ?? null);
  const [appointmentId, setAppointmentId] = useState<number | null>(null);
  const [preAuthorizationId, setPreAuthorizationId] = useState<number | null>(null);
  const [activePreAuths, setActivePreAuths] = useState<PreAuthorization[]>([]);
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [currentIllness, setCurrentIllness] = useState('');

  // Set the doctor to the logged-in user's own doctor once the auth
  // context finishes loading. The useState initializer above runs once at
  // mount; if the AuthContext's user state wasn't populated yet, the
  // initial value is null. This effect backfills it without overriding
  // a doctor that was set later (e.g., from an appointment link).
  useEffect(() => {
    if (user?.doctor?.id && doctorId === null) {
      setDoctorId(user.doctor.id);
    }
  }, [user, doctorId]);
  const [physicalExamination, setPhysicalExamination] = useState('');
  const [generalRecommendations, setGeneralRecommendations] = useState('');
  const [companionName, setCompanionName] = useState('');
  const [companionRelationship, setCompanionRelationship] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [requiresAdmission, setRequiresAdmission] = useState(false);
  const [admissionReason, setAdmissionReason] = useState('');
  const [consultationModality, setConsultationModality] = useState<string>('presencial');
  const [videoCallLink, setVideoCallLink] = useState<string | null>(null);
  const [specialtyData, setSpecialtyData] = useState<Record<string, string>>({});

  // Vital signs
  const [vitalSigns, setVitalSigns] = useState<VitalSignsEntry>(emptyVitalSigns);
  // id of the consultation's existing vital-signs record, if one was loaded —
  // lets edit-mode submit distinguish "update this record" from "create one".
  const [vitalSignsId, setVitalSignsId] = useState<number | null>(null);
  const [diagnoses, setDiagnoses] = useState<DiagnosisEntry[]>([]);
  const [prescriptions, setPrescriptions] = useState<PrescriptionEntry[]>([]);
  // Snapshots of the ids present when an existing consultation was loaded —
  // used in edit-mode submit to detect which diagnoses/prescriptions/orders
  // were removed from the form and need to be deleted server-side too.
  const [originalDiagnosisIds, setOriginalDiagnosisIds] = useState<number[]>([]);
  const [originalPrescriptionIds, setOriginalPrescriptionIds] = useState<number[]>([]);
  const [originalLabOrderIds, setOriginalLabOrderIds] = useState<number[]>([]);
  const [originalImagingOrderIds, setOriginalImagingOrderIds] = useState<number[]>([]);
  const [labOrders, setLabOrders] = useState<LabOrderEntry[]>([
    { test_id: null, test_name: '', panel_id: null, urgency: 'routine', instructions: '' },
  ]);
  const [imagingOrders, setImagingOrders] = useState<ImagingOrderEntry[]>([
    { imaging_study_id: 0, imaging_study_name: '', clinical_indication: '', priority: 'routine' },
  ]);

  const [patientBirthDate, setPatientBirthDate] = useState<string | null>(null);
  const [patientGender, setPatientGender] = useState<boolean | null>(null);

  // SOAP fields (Historia Clínica estructurada)
  const [soapFields, setSoapFields] = useState({ subjective: '', objective: '', assessment: '', plan: '' });

  // Review of Systems — initialized to defaults so the checklist renders all 14 systems
  const [reviewOfSystems, setReviewOfSystems] = useState<ReviewOfSystem[]>(() => getDefaultReviewOfSystems());

  // Autosave state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Draft recovery prompt — when non-null, the page should render the
  // ConfirmModal offering to recover the saved draft. Replaces the
  // previous window.confirm() call (audit CRITICAL X2).
  const [draftRecoveryPrompt, setDraftRecoveryPrompt] = useState<any | null>(null);

  // Search states
  const [patients, setPatients] = useState<ServicePatient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [diagnosisResults, setDiagnosisResults] = useState<Diagnosis[]>([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [searchingDiagnoses, setSearchingDiagnoses] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingPreAuths, setLoadingPreAuths] = useState(false);

  // clinical-delegation: doctors the current "Asistente Médico" is actively
  // assigned to. Empty (and inert) for everyone else — task 3.1/3.2.
  const [myDoctors, setMyDoctors] = useState<AssignedDoctor[]>([]);
  useEffect(() => {
    clinicalDelegationService.getMyDoctors().then(setMyDoctors);
  }, []);

  // clinical-record-signing R6/R15: the loaded consultation's draft/sign
  // state, needed to keep an edit-mode submit from touching locked fields
  // (must-fix item e).
  const [consultationStatus, setConsultationStatus] = useState<'draft' | 'signed' | 'amended' | null>(null);
  const [consultationLockVersion, setConsultationLockVersion] = useState<number | null>(null);
  // Who recorded it: an assistant may sign only their own draft (R12).
  const [consultationCreatedBy, setConsultationCreatedBy] = useState<number | null>(null);

  // ─── Handlers ───────────────────────────────────────────────

  const loadActivePreAuths = async (pid: number) => {
    setLoadingPreAuths(true);
    try {
      const data = await preAuthorizationService.getActiveByPatient(pid);
      setActivePreAuths(data);
    } catch {
      setActivePreAuths([]);
    } finally {
      setLoadingPreAuths(false);
    }
  };

  // Only the response to the latest search may touch state: a slow earlier
  // request must not overwrite the results for what the user typed since.
  const patientSearchSeq = useRef(0);

  const searchPatients = async (term: string) => {
    const seq = ++patientSearchSeq.current;
    if (term.trim().length < 2) {
      setPatients([]);
      setSearchingPatients(false);
      return;
    }
    setSearchingPatients(true);
    try {
      const results = await patientService.search(term);
      if (seq === patientSearchSeq.current) setPatients(results || []);
    } catch (err) {
      console.error('Error searching patients:', err);
    } finally {
      if (seq === patientSearchSeq.current) setSearchingPatients(false);
    }
  };

  const fetchDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const response = await doctorService.getAll(100);
      setDoctors(response.data || []);
    } catch (err) {
      console.error('Error fetching doctors:', err);
    } finally {
      setLoadingDoctors(false);
    }
  };

  const searchDiagnoses = async (term: string) => {
    if (term.length < 2) return;
    setSearchingDiagnoses(true);
    try {
      const results = await diagnosisService.search(term);
      setDiagnosisResults(results || []);
    } catch (err) {
      console.error('Error searching diagnoses:', err);
    } finally {
      setSearchingDiagnoses(false);
    }
  };

  const addDiagnosis = (diagnosis: Diagnosis, type: string) => {
    setDiagnoses(prev => [
      ...prev,
      {
        diagnosis_id: diagnosis.id,
        diagnosis_type: type,
        observations: '',
        diagnosis,
      },
    ]);
    setDiagnosisResults([]);
  };

  const removeDiagnosis = (index: number) => {
    setDiagnoses(prev => prev.filter((_, i) => i !== index));
  };

  const updateDiagnosisObservation = (index: number, observations: string) => {
    setDiagnoses(prev =>
      prev.map((d, i) => (i === index ? { ...d, observations } : d))
    );
  };

  const addPrescription = (medicationId: number | null = null, medicationName = '') => {
    setPrescriptions(prev => [
      ...prev,
      { medication_id: medicationId, medication_name: medicationName, dosage: '', frequency: '', duration: '', instructions: '' },
    ]);
  };

  const removePrescription = (index: number) => {
    setPrescriptions(prev => prev.filter((_, i) => i !== index));
  };

  const updatePrescription = (index: number, field: keyof PrescriptionEntry, value: string | number) => {
    setPrescriptions(prev =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );
  };

  const updateVitalSign = (field: keyof VitalSignsEntry, value: string) => {
    setVitalSigns(prev => ({ ...prev, [field]: value }));
  };

  // ─── Shared consultation loading ────────────────────────────

  const fetchConsultationAndSetShared = async (id: number) => {
    setLoadingEdit(true);
    try {
      const c = await consultationService.getById(id);
      setConsultationStatus(c.status ?? null);
      setConsultationLockVersion(c.lock_version ?? null);
      setConsultationCreatedBy(c.created_by ?? null);
      setLoadedDoctor(c.doctor ?? null);
      setPatientId(c.patient_id);
      const toLabel = (p: { first_name: string; last_name: string; identification?: string | null }) =>
        `${p.first_name} ${p.last_name} - ${p.identification || ''}`;
      const labelSeq = ++patientLabelSeq.current;
      if (c.patient) {
        setLoadedPatientLabel(toLabel(c.patient));
      } else if (c.patient_id) {
        // Not embedded in the response: fetch it without blocking the rest of the load.
        patientService.getById(c.patient_id)
          .then((p) => { if (labelSeq === patientLabelSeq.current) setLoadedPatientLabel(toLabel(p)); })
          .catch((e) => console.error('Error loading patient:', e));
      }
      setDoctorId(c.doctor_id);
      setAppointmentId(c.appointment_id || null);
      setChiefComplaint(c.chief_complaint || '');
      setCurrentIllness(c.present_illness || '');
      setPhysicalExamination(c.physical_examination || '');
      setGeneralRecommendations(c.general_recommendations || '');
      setCompanionName(c.companion_name || '');
      setCompanionRelationship(c.companion_relationship || '');
      setFollowUpDate(c.follow_up_date ? c.follow_up_date.substring(0, 10) : '');
      setRequiresAdmission(c.requires_admission || false);
      setConsultationModality(c.consultation_modality || 'presencial');
      setVideoCallLink(c.video_call_link || null);
      if (c.specialty_data) {
        setSpecialtyData(c.specialty_data);
      }
      if (c.patient?.birth_date) {
        setPatientBirthDate(c.patient.birth_date);
      }
      if (c.patient?.gender !== undefined) {
        setPatientGender(c.patient.gender);
      }
      if (c.vital_signs) {
        setVitalSignsId(c.vital_signs.id ?? null);
        setVitalSigns({
          heart_rate: c.vital_signs.heart_rate?.toString() || '',
          respiratory_rate: c.vital_signs.respiratory_rate?.toString() || '',
          systolic_pressure: c.vital_signs.systolic_pressure?.toString() || '',
          diastolic_pressure: c.vital_signs.diastolic_pressure?.toString() || '',
          temperature: c.vital_signs.temperature?.toString() || '',
          weight: c.vital_signs.weight?.toString() || '',
          height: c.vital_signs.height?.toString() || '',
          oxygen_saturation: c.vital_signs.oxygen_saturation?.toString() || '',
        });
      }
      if (c.diagnoses?.length) {
        setDiagnoses(c.diagnoses.map((d: any) => ({
          id: d.id,
          diagnosis_id: d.diagnosis_id,
          diagnosis_type: d.type || 'principal',
          observations: d.observations || '',
          diagnosis: d.diagnosis,
        })));
        setOriginalDiagnosisIds(c.diagnoses.map((d: any) => d.id).filter(Boolean));
      }
      if (c.prescriptions?.length) {
        setPrescriptions(c.prescriptions.map((p: any) => ({
          id: p.id,
          medication_id: null,
          medication_name: p.medication_name || '',
          dosage: p.dosage || '',
          frequency: p.frequency || '',
          duration: p.duration || '',
          instructions: p.instructions || '',
        })));
        setOriginalPrescriptionIds(c.prescriptions.map((p: any) => p.id).filter(Boolean));
      }
      // The backend serializes the labOrders/imagingOrders Eloquent relations
      // as lab_orders/imaging_orders (snake_case) — the MedicalConsultation
      // type's camelCase names don't match the actual runtime response, so
      // c.labOrders is always undefined. Same fallback pattern already used
      // in PatientDetailPage.tsx for this exact mismatch.
      const labOrdersData = (c as any).lab_orders || c.labOrders;
      if (labOrdersData?.length) {
        setLabOrders(labOrdersData.map((lo: any) => ({
          id: lo.id,
          test_id: lo.test_id || null,
          test_name: lo.test?.name || lo.test_name || '',
          panel_id: lo.panel_id || null,
          urgency: lo.urgency || 'routine',
          instructions: lo.instructions || '',
        })));
        setOriginalLabOrderIds(labOrdersData.map((lo: any) => lo.id).filter(Boolean));
      }
      const imagingOrdersData = (c as any).imaging_orders || c.imagingOrders;
      if (imagingOrdersData?.length) {
        setImagingOrders(imagingOrdersData.map((io: any) => ({
          id: io.id,
          imaging_study_id: io.imaging_study_id,
          imaging_study_name: io.imaging_study?.name || '',
          clinical_indication: io.clinical_indication || '',
          priority: io.priority || 'routine',
        })));
        setOriginalImagingOrderIds(imagingOrdersData.map((io: any) => io.id).filter(Boolean));
      }
      setSoapFields({
        subjective: c.subjective || '',
        objective: c.objective || '',
        assessment: c.assessment || '',
        plan: c.plan || '',
      });
      // The consultation show endpoint does not eager-load review_of_systems,
      // so it is fetched separately via the dedicated GET endpoint.
      try {
        const rosResponse = await apiClient.get<{ data: ReviewOfSystem[] }>(`/consultations/${id}/review-of-systems`);
        const ros = rosResponse.data?.data;
        setReviewOfSystems(Array.isArray(ros) && ros.length > 0 ? ros : getDefaultReviewOfSystems());
      } catch (rosErr) {
        console.error('Error loading review of systems:', rosErr);
      }
      return c;
    } catch (err) {
      console.error('Error loading consultation:', err);
      setError('Error al cargar la consulta');
      return null;
    } finally {
      setLoadingEdit(false);
    }
  };

  // ─── Autosave Draft ────────────────────────────────────────

  // Track unsaved changes when form fields change
  useEffect(() => {
    if (!isEditMode) {
      setHasUnsavedChanges(true);
    }
  }, [patientId, doctorId, chiefComplaint, currentIllness, vitalSigns, specialtyData, labOrders, imagingOrders, diagnoses, prescriptions, soapFields, reviewOfSystems, isEditMode]);

  // Autosave to localStorage every 30 seconds in new mode
  useEffect(() => {
    if (isEditMode) return;

    const timer = setInterval(() => {
      if (hasUnsavedChanges) {
        const draft = {
          patientId, doctorId, chiefComplaint, currentIllness, vitalSigns,
          specialtyData, labOrders, imagingOrders, diagnoses, prescriptions,
          soapFields, reviewOfSystems,
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
        setLastSavedAt(new Date());
      }
    }, 30000);

    return () => clearInterval(timer);
  }, [hasUnsavedChanges, patientId, doctorId, chiefComplaint, currentIllness, vitalSigns, specialtyData, labOrders, imagingOrders, diagnoses, prescriptions, soapFields, reviewOfSystems, isEditMode, editId]);

  // Restore draft on mount in new mode — surfaces the saved draft as React
  // state instead of calling window.confirm(). The page renders a ConfirmModal
  // driven by `draftRecoveryPrompt` (audit CRITICAL X2).
  useEffect(() => {
    if (isEditMode) return;

    const saved = localStorage.getItem('consultation_draft_new');
    if (!saved) return;
    try {
      const draft = JSON.parse(saved);
      if (draft && typeof draft === 'object') {
        setDraftRecoveryPrompt(draft);
      }
    } catch {
      // Ignore parse errors — silent leak is acceptable here since the
      // corrupted draft will simply be overwritten on next autosave.
    }
  }, []);

  const confirmDraftRecovery = () => {
    const draft = draftRecoveryPrompt;
    setDraftRecoveryPrompt(null);
    if (!draft) return;
    setChiefComplaint(draft.chiefComplaint || '');
    setCurrentIllness(draft.currentIllness || '');
    setVitalSigns(draft.vitalSigns || emptyVitalSigns);
    setSpecialtyData(draft.specialtyData || {});
    setLabOrders(draft.labOrders || [{ test_id: null, test_name: '', panel_id: null, urgency: 'routine', instructions: '' }]);
    setImagingOrders(draft.imagingOrders || [{ imaging_study_id: 0, imaging_study_name: '', clinical_indication: '', priority: 'routine' }]);
    setDiagnoses(draft.diagnoses || []);
    setPrescriptions(draft.prescriptions || []);
    setSoapFields(draft.soapFields || { subjective: '', objective: '', assessment: '', plan: '' });
    setReviewOfSystems(draft.reviewOfSystems || getDefaultReviewOfSystems());
  };

  const dismissDraftRecovery = () => {
    setDraftRecoveryPrompt(null);
    // Clear the saved draft so it does not leak across sessions or
    // re-prompt on next mount (audit CRITICAL X2 silent-leak fix).
    localStorage.removeItem(DRAFT_KEY);
  };

  // Clear draft on successful submit
  useEffect(() => {
    if (success) {
      localStorage.removeItem(DRAFT_KEY);
      setHasUnsavedChanges(false);
      setLastSavedAt(null);
    }
  }, [success]);

  // Warn the doctor before closing/refreshing the tab while the form is
  // dirty. Closes audit CRITICAL X3. Modern browsers ignore the custom
  // message text and show their own dialog — that's fine, the warning
  // still fires and gives the doctor a chance to cancel. In-app React
  // Router navigation is NOT caught here; addressing that needs
  // useBlocker from React Router 7 (future enhancement).
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Tiene cambios sin guardar. ¿Salir de todas formas?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // ─── Shared payload building ────────────────────────────────

  const buildSharedPayload = (): Partial<CreateConsultationRequest> | null => {
    if (!patientId || !doctorId || !chiefComplaint || !currentIllness) {
      setError('Paciente, doctor, motivo de consulta y enfermedad actual son requeridos');
      return null;
    }

    const vs: Record<string, number> = {};
    Object.entries(vitalSigns).forEach(([key, val]) => {
      const num = parseFloat(val);
      if (!isNaN(num) && num > 0) vs[key] = num;
    });

    return {
      patient_id: patientId,
      doctor_id: doctorId,
      appointment_id: appointmentId || undefined,
      pre_authorization_id: preAuthorizationId || undefined,
      chief_complaint: chiefComplaint,
      present_illness: currentIllness,
      physical_examination: physicalExamination || undefined,
      general_recommendations: generalRecommendations || undefined,
      companion_name: companionName || undefined,
      companion_relationship: companionRelationship || undefined,
      follow_up_date: followUpDate || undefined,
      requires_admission: requiresAdmission,
      admission_reason: requiresAdmission ? admissionReason : undefined,
      consultation_modality: consultationModality !== 'presencial' ? consultationModality : undefined,
      vital_signs: Object.keys(vs).length > 0 ? vs : undefined,
      diagnoses: diagnoses.length > 0
        ? diagnoses.map(d => ({
            diagnosis_id: d.diagnosis_id,
            type: d.diagnosis_type,
            observations: d.observations || undefined,
          }))
        : undefined,
      prescriptions: prescriptions.length > 0
        ? prescriptions.filter(p => p.medication_name.trim())
        : undefined,
      subjective: soapFields.subjective || undefined,
      objective: soapFields.objective || undefined,
      assessment: soapFields.assessment || undefined,
      plan: soapFields.plan || undefined,
    };
  };

  const getLegacySpecialtyData = () => specialtyData;

  return {
    // State
    loading, setLoading,
    loadingEdit, setLoadingEdit,
    error, setError,
    success, setSuccess,
    rosSaveWarning, setRosSaveWarning,
    isEditMode,
    hasUnsavedChanges,
    lastSavedAt,
    // Draft recovery prompt (audit CRITICAL X2)
    draftRecoveryPrompt,
    confirmDraftRecovery,
    dismissDraftRecovery,
    patientId, setPatientId,
    doctorId, setDoctorId,
    appointmentId, setAppointmentId,
    preAuthorizationId, setPreAuthorizationId,
    activePreAuths, loadActivePreAuths,
    chiefComplaint, setChiefComplaint,
    currentIllness, setCurrentIllness,
    physicalExamination, setPhysicalExamination,
    generalRecommendations, setGeneralRecommendations,
    companionName, setCompanionName,
    companionRelationship, setCompanionRelationship,
    followUpDate, setFollowUpDate,
    requiresAdmission, setRequiresAdmission,
    admissionReason, setAdmissionReason,
    consultationModality, setConsultationModality,
    videoCallLink,
    specialtyData, setSpecialtyData,
    patientBirthDate, setPatientBirthDate,
    patientGender, setPatientGender,
    doctorSpecialty: user?.doctor?.specialty?.name ?? null,
    currentDoctor: user?.doctor
      ? {
          id: user.doctor.id,
          first_name: user.doctor.first_name,
          last_name: user.doctor.last_name,
          specialty: user.doctor.specialty,
        }
      : null,
    // SOAP fields
    soapFields, setSoapFields,
    // Review of Systems
    reviewOfSystems, setReviewOfSystems,
    // Vital signs
    vitalSigns, updateVitalSign, vitalSignsId,
    // Diagnoses
    diagnoses, addDiagnosis, removeDiagnosis, updateDiagnosisObservation,
    diagnosisResults, searchDiagnoses, searchingDiagnoses,
    originalDiagnosisIds,
    // Prescriptions
    prescriptions, addPrescription, removePrescription, updatePrescription,
    originalPrescriptionIds,
    // Lab and Imaging orders
    labOrders, setLabOrders,
    imagingOrders, setImagingOrders,
    originalLabOrderIds, originalImagingOrderIds,
    // Search
    patients, searchPatients, searchingPatients,
    loadedPatientLabel, clearLoadedPatientLabel,
    setPatients,
    doctors, fetchDoctors,
    loadingDoctors, loadingPreAuths,
    // clinical-delegation / clinical-record-signing
    myDoctors,
    isAssistant,
    loadedDoctor,
    consultationStatus,
    consultationLockVersion,
    consultationCreatedBy,
    // Shared loading / building / submit
    fetchConsultationAndSetShared,
    buildSharedPayload,
    getLegacySpecialtyData,
    navigate,
  };
};
